import React, { useState, useEffect } from "react";
import { getMortgageConfig } from "../utils/mortgageRatesApi";
import {
  PI_COLUMN_TERM,
  IO_COLUMN_TERM,
  DEFAULT_MANAGEMENT_FEE_PERCENT,
  MANAGEMENT_FEE_START_YEAR,
} from "../utils/rentalAnalysisCalculator";
import { authFetch } from "../api";
import CurrencyInput from "./CurrencyInput";

export default function MortgageConfigModal({ isOpen, onClose, propertyPrice, onSave, propertyId }) {
  // State for mortgage configuration
  const [downPaymentPercent, setDownPaymentPercent] = useState(25);
  const [downPaymentDollarAmount, setDownPaymentDollarAmount] = useState(null); // Store actual dollar amount entered by user
  const [mortgageTerm, setMortgageTerm] = useState("7/1 ARM");
  const [paymentType, setPaymentType] = useState("I/O");
  const [mortgageRate, setMortgageRate] = useState(7.0);

  // Property management fee. On by default at 5% -- see resolveManagementFeePercent for why
  // the default is "yes" and why a listing saved before this existed still gets it.
  const [managementFeeEnabled, setManagementFeeEnabled] = useState(true);
  const [managementFeePercent, setManagementFeePercent] = useState(DEFAULT_MANAGEMENT_FEE_PERCENT);

  // State for dollar input field (separate from calculated value to avoid rounding issues)

  // State for config info
  const [configData, setConfigData] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // State for save feedback
  const [saveStatus, setSaveStatus] = useState(null); // null | 'success'

  // Whether THIS listing has a saved rate override. Once set, the listing stops tracking the
  // global rate in Settings, so the analyst needs to be told it is on and given a way off.
  const [hasRateOverride, setHasRateOverride] = useState(false);

  // Load mortgage config and saved analysis on modal open
  useEffect(() => {
    if (isOpen) {
      const loadData = async () => {
        await loadConfig();
        await loadSavedAnalysis();
      };
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, propertyId]);

  // Load config from backend (cached in memory)
  const loadConfig = async () => {
    setLoadingConfig(true);
    try {
      const response = await getMortgageConfig(propertyId);
      setConfigData(response.data);

      // Set down payment from config (will be overwritten if saved analysis has it)
      if (response.data.down_payment_percent) {
        setDownPaymentPercent(response.data.down_payment_percent);
      }

      // Auto-populate rate based on current mortgage term and payment type
      updateRateForTermAndType(mortgageTerm, paymentType, response.data);
    } catch (error) {
      console.error("Failed to load mortgage config:", error);
    } finally {
      setLoadingConfig(false);
    }
  };

  // Load saved mortgage analysis data
  const loadSavedAnalysis = async () => {
    if (!propertyId) {
      console.log('⚠️ No propertyId, skipping loadSavedAnalysis');
      return;
    }

    console.log('🔍 Loading saved mortgage analysis for property:', propertyId);

    try {
      const response = await authFetch(`/listings/${propertyId}/analysis`);
      console.log('📨 Saved analysis response:', response);

      if (response && response.analysis) {
        const analysis = response.analysis;
        console.log('✅ Found saved analysis:', analysis);

        // Load saved mortgage configuration if it exists
        if (analysis.down_payment_percent !== undefined) {
          console.log('📝 Setting down payment percent:', analysis.down_payment_percent);
          setDownPaymentPercent(analysis.down_payment_percent);
        }
        // NOT down_payment_amount. `downPaymentDollarAmount` exists so an analyst can type an
        // exact dollar figure during a session; seeding it from the saved blob turned that
        // session-local override into a permanent pin, and the pinned value was computed
        // against whatever the price was when Save was last pressed. On listing 1356079 it
        // held $248,750 -- 25% of the UNDISCOUNTED $995,000 -- so reopening this modal after
        // a 1% discount showed $248,750 where every other surface on the page had derived
        // $246,263, and the loan it implied ($736,300) matched nothing at all.
        //
        // down_payment_percent is saved too and IS the stored truth: computedValues, the Out
        // of Pocket box, the cap rate and the IRR all derive from it. Deriving here as well
        // is what makes this modal agree with the page it configures. A dollar figure typed
        // into the field below writes the equivalent percent back, so intent survives to that
        // field's 2-decimal rounding.
        if (analysis.mortgage_term) {
          console.log('📝 Setting mortgage term:', analysis.mortgage_term);
          setMortgageTerm(analysis.mortgage_term);
        }
        if (analysis.payment_type) {
          console.log('📝 Setting payment type:', analysis.payment_type);
          setPaymentType(analysis.payment_type);
        }
        // Explicit !== undefined: `false` and `0` are both meaningful saved values here, so a
        // truthiness check would silently reinstate the default for anyone who switched the
        // fee off or set it to zero.
        if (analysis.management_fee_enabled !== undefined && analysis.management_fee_enabled !== null) {
          setManagementFeeEnabled(!!analysis.management_fee_enabled);
        }
        if (analysis.management_fee_percent !== undefined && analysis.management_fee_percent !== null) {
          setManagementFeePercent(analysis.management_fee_percent);
        }
        if (analysis.mortgage_rate !== undefined && analysis.mortgage_rate !== null) {
          console.log('📝 Setting mortgage rate:', analysis.mortgage_rate);
          setMortgageRate(analysis.mortgage_rate);
          setHasRateOverride(true);
        } else {
          setHasRateOverride(false);
        }
      } else {
        setHasRateOverride(false);
        console.log('⚠️ No saved analysis data');
      }
    } catch (error) {
      console.log("❌ No saved mortgage analysis found, using defaults:", error);
    }
  };

  // Update rate when mortgage term changes
  const handleMortgageTermChange = (newTerm) => {
    setMortgageTerm(newTerm);
    if (configData) {
      updateRateForTermAndType(newTerm, paymentType, configData);
    }
  };

  // Update rate when payment type changes
  const handlePaymentTypeChange = (newPaymentType) => {
    setPaymentType(newPaymentType);
    if (configData) {
      updateRateForTermAndType(mortgageTerm, newPaymentType, configData);
    }
  };

  // Map mortgage term and payment type to rate and update state
  const updateRateForTermAndType = (term, payment, config) => {
    if (!config || !config.rates) return;

    // Determine payment type key: "io" or "pi"
    const paymentKey = payment === "I/O" ? "io" : "pi";

    // Determine term key based on mortgage term
    let termKey;
    if (term === "30yr Fixed") {
      termKey = "30_year_fixed";
    } else if (term === "7/1 ARM") {
      termKey = "7_1_arm";
    } else if (term === "10/1 ARM") {
      termKey = "10_1_arm";
    }

    // Get rate from config: config.rates[paymentKey][termKey]
    if (termKey && config.rates[paymentKey] && config.rates[paymentKey][termKey]) {
      setMortgageRate(config.rates[paymentKey][termKey]);
    }
  };

  // Calculated values
  // Use the stored dollar amount if user entered it directly, otherwise calculate from percentage
  const downPaymentAmount = downPaymentDollarAmount !== null
    ? downPaymentDollarAmount
    : propertyPrice * (downPaymentPercent / 100);
  const loanAmount = propertyPrice - downPaymentAmount;

  // Calculate monthly mortgage payment
  const calculateMonthlyPayment = () => {
    if (loanAmount <= 0) return 0;

    const monthlyRate = mortgageRate / 100 / 12;

    if (paymentType === "I/O") {
      // Interest Only: just pay the interest each month
      return loanAmount * monthlyRate;
    } else {
      // Principal + Interest (Amortizing)
      let numberOfPayments;

      // For ARMs, calculate based on initial fixed period
      // But for simplicity, we'll use 30 years for all
      if (mortgageTerm === "30yr Fixed") {
        numberOfPayments = 30 * 12;
      } else if (mortgageTerm === "10/1 ARM") {
        numberOfPayments = 30 * 12; // Amortized over 30 years
      } else { // 7/1 ARM
        numberOfPayments = 30 * 12; // Amortized over 30 years
      }

      // Standard mortgage formula: M = P[r(1+r)^n]/[(1+r)^n-1]
      const monthlyPayment = loanAmount *
        (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

      return monthlyPayment;
    }
  };

  const monthlyPayment = calculateMonthlyPayment();

  // Does this combination actually drive the Rental Value Analysis? Mirrors the strict
  // type+term match in calculateRentalAnalysis — the two must agree or the banner lies.
  const affectsRVA =
    (paymentType === "P&I" && mortgageTerm === PI_COLUMN_TERM) ||
    (paymentType === "I/O" && mortgageTerm === IO_COLUMN_TERM);

  // Handle save - only save, don't close
  const handleSave = () => {
    console.log('💾 handleSave called');
    console.log('💾 downPaymentDollarAmount state:', downPaymentDollarAmount);
    console.log('💾 downPaymentAmount (calculated):', downPaymentAmount);
    console.log('💾 downPaymentPercent:', downPaymentPercent);

    onSave({
      downPaymentPercent,
      downPaymentAmount,
      mortgageTerm,
      paymentType,
      mortgageRate,
      loanAmount,
      monthlyPayment,
      managementFeeEnabled,
      managementFeePercent,
    });

    setHasRateOverride(true);

    // Show success message
    setSaveStatus('success');

    // Clear success message after 2 seconds
    setTimeout(() => {
      setSaveStatus(null);
    }, 2000);
  };

  // Handle save and close
  const handleSaveAndClose = () => {
    onSave({
      downPaymentPercent,
      downPaymentAmount,
      mortgageTerm,
      paymentType,
      mortgageRate,
      loanAmount,
      monthlyPayment,
      managementFeeEnabled,
      managementFeePercent,
    });
    onClose();
  };

  // Drop this listing's rate override so it tracks the global Settings rate again.
  // Nulls rather than omissions: handleMortgageSave spreads the existing analysis, so an
  // omitted key would keep the old value and the "reset" would do nothing.
  const handleResetToGlobal = () => {
    onSave({
      downPaymentPercent,
      downPaymentAmount,
      mortgageTerm: null,
      paymentType: null,
      mortgageRate: null,
      loanAmount: null,
      monthlyPayment: null,
      // Not part of the rate override -- "reset to global" is about the mortgage rate, and
      // clearing the management fee here would quietly undo a separate decision.
      managementFeeEnabled,
      managementFeePercent,
    });
    setHasRateOverride(false);
    if (configData) updateRateForTermAndType(mortgageTerm, paymentType, configData);
    setSaveStatus('success');
    setTimeout(() => setSaveStatus(null), 2000);
  };

  // Handle close without saving
  const handleClose = () => {
    setSaveStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  // Styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  };

  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "600px",
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    borderBottom: "2px solid #000",
    paddingBottom: "16px",
  };

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#333",
  };

  const closeButtonStyle = {
    fontSize: "28px",
    fontWeight: "700",
    cursor: "pointer",
    border: "none",
    background: "none",
    color: "#666",
    padding: "0",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const formGroupStyle = {
    marginBottom: "24px",
  };

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "8px",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px",
    border: "2px solid #ddd",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  const summaryBoxStyle = {
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "24px",
    border: "2px solid #000",
  };

  const summaryRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px",
    fontSize: "16px",
  };

  const summaryLabelStyle = {
    color: "#666",
  };

  const summaryValueStyle = {
    fontWeight: "700",
    color: "#333",
  };

  const buttonContainerStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: "24px",
  };

  const buttonStyle = {
    padding: "12px 24px",
    borderRadius: "6px",
    border: "none",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  };

  const saveButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#000",
    color: "#fff",
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#f8f9fa",
    color: "#333",
    border: "2px solid #ddd",
  };

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>Mortgage Configuration</h2>
          <button style={closeButtonStyle} onClick={handleClose}>×</button>
        </div>

        {/* Summary Box */}
        <div style={summaryBoxStyle}>
          <div style={summaryRowStyle}>
            <span style={summaryLabelStyle}>Property Price:</span>
            <span style={summaryValueStyle}>${propertyPrice.toLocaleString()}</span>
          </div>
          <div style={summaryRowStyle}>
            <span style={summaryLabelStyle}>Down Payment ({Number(downPaymentPercent).toFixed(2)}%):</span>
            <span style={summaryValueStyle}>${Math.round(downPaymentAmount).toLocaleString()}</span>
          </div>
          <div style={summaryRowStyle}>
            <span style={summaryLabelStyle}>Loan Amount:</span>
            <span style={summaryValueStyle}>${Math.round(loanAmount).toLocaleString()}</span>
          </div>
          <div style={{
            ...summaryRowStyle,
            marginTop: "16px",
            paddingTop: "16px",
            borderTop: "2px solid #ddd",
            fontSize: "18px",
          }}>
            <span style={summaryLabelStyle}>Monthly Payment:</span>
            <span style={{ ...summaryValueStyle, color: "#28a745" }}>
              ${Math.round(monthlyPayment).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Form Fields */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Down Payment (%)</label>
            <input
              type="number"
              style={inputStyle}
              value={downPaymentPercent}
              onChange={(e) => {
                const value = e.target.value;
                console.log('📊 Down Payment (%) onChange - value:', value);
                // Clear the stored dollar amount when percentage is changed directly
                setDownPaymentDollarAmount(null);
                // Allow empty string for easier editing
                if (value === '') {
                  console.log('📊 Empty value, setting to 0');
                  setDownPaymentPercent(0);
                  return;
                }
                const percent = parseFloat(value);
                if (!isNaN(percent)) {
                  const clampedPercent = Math.max(0, Math.min(100, percent));
                  console.log('📊 Setting percent to:', clampedPercent);
                  setDownPaymentPercent(clampedPercent);
                }
              }}
              onBlur={(e) => {
                // Format to 2 decimal places on blur
                const percent = parseFloat(e.target.value) || 0;
                const clampedPercent = Math.max(0, Math.min(100, percent));
                setDownPaymentPercent(parseFloat(clampedPercent.toFixed(2)));
              }}
              min="0"
              max="100"
              step="0.01"
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Down Payment ($)</label>
            <CurrencyInput
              style={inputStyle}
              value={Math.round(downPaymentAmount)}
              onChange={(v) => {
                // Empty → treat as 0% down, clear the explicit dollar amount
                if (v === "") {
                  setDownPaymentPercent(0);
                  setDownPaymentDollarAmount(null);
                  return;
                }
                const clampedAmount = Math.max(0, Math.min(propertyPrice, v));
                // Store the exact dollar amount entered by user (takes precedence over %)
                setDownPaymentDollarAmount(clampedAmount);
                const newPercent = propertyPrice > 0 ? (clampedAmount / propertyPrice) * 100 : 0;
                setDownPaymentPercent(parseFloat(newPercent.toFixed(2)));
              }}
            />
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Mortgage Term</label>
          <select
            style={selectStyle}
            value={mortgageTerm}
            onChange={(e) => handleMortgageTermChange(e.target.value)}
          >
            {paymentType === "P&I" && <option value="30yr Fixed">30 Year Fixed</option>}
            <option value="7/1 ARM">7/1 ARM</option>
            <option value="10/1 ARM">10/1 ARM</option>
          </select>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Payment Type</label>
          <select
            style={selectStyle}
            value={paymentType}
            onChange={(e) => handlePaymentTypeChange(e.target.value)}
          >
            <option value="I/O">Interest Only (I/O)</option>
            <option value="P&I">Principal & Interest (P&I)</option>
          </select>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Mortgage Rate (%)
          </label>
          <input
            type="number"
            style={inputStyle}
            value={mortgageRate}
            onChange={(e) => setMortgageRate(parseFloat(e.target.value) || 0)}
            min="0"
            max="20"
            step="0.125"
          />
          <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
            {loadingConfig ? "Loading config..." : "Rate auto-populated from config - adjust as needed"}
          </div>
        </div>

        {/* Property management fee. It only ever reaches the IRR -- the fee starts in year
            {MANAGEMENT_FEE_START_YEAR} and the Rental Value Analysis' cashflow and cap rate are
            year-1 figures -- so the hint says so rather than letting the analyst expect the
            whole table to move. */}
        <div style={formGroupStyle}>
          <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              checked={managementFeeEnabled}
              onChange={(e) => setManagementFeeEnabled(e.target.checked)}
              style={{ width: "16px", height: "16px", cursor: "pointer", margin: 0 }}
            />
            Management Fee
            <span style={{ fontWeight: "400", color: "#666" }}>
              (from year {MANAGEMENT_FEE_START_YEAR})
            </span>
          </label>
          <input
            type="number"
            style={{ ...inputStyle, opacity: managementFeeEnabled ? 1 : 0.5 }}
            value={managementFeePercent}
            disabled={!managementFeeEnabled}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                setManagementFeePercent(0);
                return;
              }
              const pct = parseFloat(value);
              if (!isNaN(pct)) setManagementFeePercent(Math.max(0, Math.min(100, pct)));
            }}
            min="0"
            max="100"
            step="0.25"
          />
          <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
            {managementFeeEnabled
              ? `${managementFeePercent}% of rent collected, charged from year ${MANAGEMENT_FEE_START_YEAR} onwards. Affects the IRR only — Net Cashflow and Cap Rate are year-1 figures.`
              : "No management fee is charged in the projection."}
          </div>
        </div>

        {/* Override banner. Two different things to say, because a saved rate only reaches the
            Rental Value Analysis when it was entered for one of the two financings that table
            reports on. Saved against any other combination it still persists (the IRR modal and
            Out of Pocket use it) but the RVA columns keep the global rate — and claiming
            otherwise would send someone hunting for a number that was never going to move. */}
        {hasRateOverride && (
          <div style={{
            marginTop: "16px",
            padding: "12px 14px",
            backgroundColor: affectsRVA ? "#fff8e1" : "#eef4fb",
            border: `1px solid ${affectsRVA ? "#ffe082" : "#cfe0f3"}`,
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}>
            <div style={{ fontSize: "13px", color: affectsRVA ? "#6d4c00" : "#28527a", lineHeight: 1.45 }}>
              {affectsRVA ? (
                <>
                  <strong>Custom rate for this listing.</strong> {mortgageRate}% is used for the{" "}
                  {paymentType} column of the Rental Value Analysis instead of the global rate, and
                  changes in Settings will not affect it.
                </>
              ) : (
                <>
                  <strong>Saved for this listing, but not shown in the Rental Value Analysis.</strong>{" "}
                  That table only reports P&amp;I on the {PI_COLUMN_TERM} and I/O on the{" "}
                  {IO_COLUMN_TERM}, so {mortgageRate}% on {paymentType} / {mortgageTerm} leaves its
                  columns on the global rate.
                </>
              )}
            </div>
            <button
              onClick={handleResetToGlobal}
              style={{
                flexShrink: 0,
                padding: "6px 12px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#6d4c00",
                backgroundColor: "#fff",
                border: "1px solid #e0b900",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Reset to global
            </button>
          </div>
        )}

        {/* Save Status Message */}
        {saveStatus === 'success' && (
          <div style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#d4edda",
            color: "#155724",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}>
            <span style={{ fontSize: "18px" }}>✓</span>
            <span>Saved successfully!</span>
          </div>
        )}

        {/* Action Buttons */}
        <div style={buttonContainerStyle}>
          <button style={cancelButtonStyle} onClick={handleClose}>
            Cancel
          </button>
          <button style={saveButtonStyle} onClick={handleSave}>
            Save
          </button>
          <button style={saveButtonStyle} onClick={handleSaveAndClose}>
            Save and Close
          </button>
        </div>
      </div>
    </div>
  );
}
